import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { useDrawerDrag } from '../../../hooks/useDrawerDrag';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import FormInput from '../../../components/FormInput/FormInput';
import Toggle from '../../../components/Toggle/Toggle';
import PageFooter from '../../../components/PageFooter/PageFooter';
import api from '../../../services/api';
import styles from './AccountSettings.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const readUser = () => JSON.parse(localStorage.getItem('user') || '{}');
const fullNameOf = u => [u.firstName, u.lastName].filter(Boolean).join(' ');

function AccountSettings() {
  usePageTitle('Account Settings');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const storedUser = readUser();
  const email = storedUser.email || '';

  // The saved* values are the baseline the form is diffed against; they move
  // forward on a successful save so the button hides again.
  const [savedName, setSavedName]         = useState(() => fullNameOf(storedUser));
  const [savedRecovery, setSavedRecovery] = useState(() => storedUser.recoveryContactEmail || '');
  const [name, setName]                   = useState(savedName);
  const [recoveryEmail, setRecovery]      = useState(savedRecovery);
  const [saving, setSaving]               = useState(false);
  const [saveError, setSaveError]         = useState('');
  const [infoOpen, setInfoOpen]           = useState(false);

  const {
    modalRef: sheetRef,
    close: closeSheet,
    dragHandleProps: sheetDragHandle,
  } = useDrawerDrag(() => setInfoOpen(false));

  const isDirty = name.trim() !== savedName || recoveryEmail.trim() !== savedRecovery;

  const handleToggle = () => {
    const next = !darkMode;
    setDarkMode(next);
    const theme = next ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  };

  // PATCH /auth/me for the name, PATCH|DELETE /auth/me/recovery for the recovery
  // contact — only the fields that actually changed are sent.
  async function handleSave() {
    const nextName     = name.trim();
    const nextRecovery = recoveryEmail.trim();

    if (nextRecovery && !EMAIL_RE.test(nextRecovery)) {
      setSaveError('Please enter a valid recovery email address.');
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      const patch = {};

      if (nextName !== savedName) {
        const parts     = nextName.split(' ');
        const firstName = parts[0] || '';
        const lastName  = parts.slice(1).join(' ') || '';
        await api.patch('/auth/me', { firstName, lastName });
        patch.firstName = firstName;
        patch.lastName  = lastName;
      }

      if (nextRecovery !== savedRecovery) {
        if (nextRecovery) {
          await api.patch('/auth/me/recovery', { recoveryEmail: nextRecovery });
        } else {
          await api.delete('/auth/me/recovery');
        }
        patch.recoveryContactEmail = nextRecovery;
      }

      localStorage.setItem('user', JSON.stringify({ ...readUser(), ...patch }));
      setSavedName(nextName);
      setSavedRecovery(nextRecovery);
      setName(nextName);
      setRecovery(nextRecovery);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
    <DashboardLayout className={styles.layout}>
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Account settings</h1>

        <FormInput
          id="fullName"
          label="Full name"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
        />

        <div className={styles.emailWrapper}>
          <FormInput
            id="email"
            label="Email"
            type="email"
            value={email}
            readOnly
          />
        </div>

        <div className={styles.fieldGroup}>
          <label htmlFor="recoveryEmail" className={styles.recoveryLabel}>
            Recovery email
            <button className={styles.infoBtn} type="button" onClick={() => setInfoOpen(true)}>
              <Info size={14} />
            </button>
          </label>
          <input
            id="recoveryEmail"
            type="email"
            className={styles.recoveryInput}
            placeholder="trusted-person@email.com"
            value={recoveryEmail}
            onChange={e => setRecovery(e.target.value)}
          />
        </div>

        <div className={styles.toggleRow}>
          <span className={styles.toggleLabel}>Dark mode</span>
          <Toggle checked={darkMode} onChange={handleToggle} />
        </div>

        {/* On mobile this group is pushed to the bottom of the viewport;
            on desktop it is display: contents, so nothing changes there. */}
        <div className={styles.bottomGroup}>
          {saveError && <p className={styles.saveError}>{saveError}</p>}

          {isDirty && (
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}

          <PageFooter />
        </div>
      </div>
    </DashboardLayout>
    {infoOpen && createPortal(
      <div className={styles.sheetOverlay} onClick={closeSheet}>
        <div className={styles.sheet} ref={sheetRef} onClick={e => e.stopPropagation()}>
          <div className={styles.sheetHandle} {...sheetDragHandle} />
          <h2 className={styles.sheetTitle}>What's a recovery email?</h2>
          <p className={styles.sheetDesc}>
            If you're ever unable to access your account, your recovery contact can request temporary access on your behalf. Any withdrawal requests made by them will be notified to your email. From there, they can request permanent access if needed, and our team will review every request carefully before anything is granted.
          </p>
          <p className={styles.sheetDesc}>
            This is optional and you can update or remove your recovery contact at any time.
          </p>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}

export default AccountSettings;
